import React, { useCallback, useMemo } from 'react';
import { FlatList, StyleSheet, ListRenderItem } from 'react-native';
import { ChatGlassConversationCard } from '@/components/shared/redesign';
import { IConversation } from '@/types/admin/messages';
import { PERFORMANCE_CONSTANTS, FLATLIST_PROPS } from '@/constants/admin/performance';
import { generateConversationKey, validateUniqueKeys } from '@/utils/admin/keyUtils';
import { useConversationUtils } from '@/hooks/admin/useMessages';

interface ConversationListProps {
  conversations: IConversation[];
  hasNextPage: boolean;
  isLoading: boolean;
  loadMore: () => void;
  selectedConversation?: number;
  onConversationSelect: (conversationId: number) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  hasNextPage,
  isLoading,
  loadMore,
  selectedConversation,
  onConversationSelect,
}) => {
  const { truncateSubject, truncateMessage, getRecipientDisplayName, formatMessageDate } =
    useConversationUtils();

  // Validate unique keys in development
  React.useEffect(() => {
    validateUniqueKeys(conversations, (conv) => conv.id, 'conversations');
  }, [conversations]);

  // Memoized handlers for performance
  const handleConversationPress = useCallback((conversationId: number | string) => {
    onConversationSelect(Number(conversationId));
  }, [onConversationSelect]);

  const keyExtractor = useCallback((item: IConversation) => generateConversationKey(item.id), []);

  const renderConversationItem: ListRenderItem<IConversation> = useCallback(({ item }) => {
    const conversationAccounts = (item.accounts || []).filter(
      (acct) => acct.ConversationAccount?.type === 'resident'
    );
    const recipientCount = conversationAccounts.length;
    const isGroup = recipientCount > 1;
    const subject = truncateSubject(item.subject || '');
    const preview = truncateMessage(item.messages?.[0]?.content || '');
    const dateRaw = item.messages?.[0]?.createdAt || item.createdAt;
    const dateLabel = dateRaw ? formatMessageDate(dateRaw) : '';
    const subtitle = getRecipientDisplayName(item);

    return (
      <ChatGlassConversationCard
        id={item.id}
        subject={subject}
        preview={preview}
        date={dateLabel}
        isGroup={isGroup}
        recipientCount={recipientCount}
        hasUnread={!!item.hasUnread}
        isSelected={selectedConversation === item.id}
        subtitle={subtitle}
        onPress={handleConversationPress}
      />
    );
  }, [
    handleConversationPress,
    selectedConversation,
    truncateSubject,
    truncateMessage,
    formatMessageDate,
    getRecipientDisplayName,
  ]);

  // Memoized getItemLayout for better performance
  const getItemLayout = useMemo(
    () => FLATLIST_PROPS.getItemLayout(PERFORMANCE_CONSTANTS.CONVERSATION_ITEM_HEIGHT),
    []
  );

  // Handle end reached for infinite scroll
  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isLoading) {
      loadMore();
    }
  }, [hasNextPage, isLoading, loadMore]);

  return (
    <FlatList
      data={conversations}
      renderItem={renderConversationItem}
      keyExtractor={keyExtractor}
      getItemLayout={getItemLayout}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.listContent}
      style={styles.container}
      // Performance optimizations
      removeClippedSubviews={FLATLIST_PROPS.removeClippedSubviews}
      maxToRenderPerBatch={FLATLIST_PROPS.maxToRenderPerBatch}
      windowSize={FLATLIST_PROPS.windowSize}
      initialNumToRender={FLATLIST_PROPS.initialNumToRender}
      updateCellsBatchingPeriod={FLATLIST_PROPS.updateCellsBatchingPeriod}
      // Infinite scroll
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 16,
  },
});
